import { useState } from "react";
import toast from "react-hot-toast";
import api from "../../services/api";

export default function PasswordResetCard({ className = "", variant = "card", title = "Privacy" }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleResetPassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      return toast.error("Please fill in all fields");
    }
    if (newPassword !== confirmPassword) {
      return toast.error("New passwords do not match");
    }
    if (newPassword.length < 6) {
      return toast.error("Password must be at least 6 characters");
    }
    if (currentPassword === newPassword) {
      return toast.error("New password must be different from current password");
    }

    toast((t) => (
      <div className="flex flex-col space-y-2">
        <span>Are you sure you want to reset your password?</span>
        <div className="flex justify-end space-x-2">
          <button
            className="bg-gray-300 px-3 py-1 rounded hover:bg-gray-400"
            onClick={() => toast.dismiss(t.id)}
          >
            Cancel
          </button>
          <button
            className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
            onClick={async () => {
              toast.dismiss(t.id);
              const toastId = toast.loading("Resetting password...");
              setLoading(true);

              try {
                const res = await api.put("/users/reset-password", {
                  currentPassword,
                  newPassword,
                  confirmPassword,
                });

                if (res.data?.success) {
                  toast.success("Password reset successfully.", { id: toastId });
                  setCurrentPassword("");
                  setNewPassword("");
                  setConfirmPassword("");
                } else {
                  toast.error(res.data?.message || "Failed to reset password", { id: toastId });
                }
              } catch (err) {
                console.error("Reset password error:", err);
                toast.error(err.response?.data?.message || "Failed to reset password.", {
                  id: toastId,
                });
              } finally {
                setLoading(false);
              }
            }}
          >
            Yes
          </button>
        </div>
      </div>
    ));
  };

  const wrapperClass =
    variant === "plain"
      ? `w-full ${className}`
      : `bg-white dark:bg-gray-800 p-6 rounded-xl shadow w-full ${className}`;

  return (
    <div className={wrapperClass}>
      <h3 className="text-lg font-semibold text-green-700 dark:text-green-200 mb-4">{title}</h3>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Current Password</label>
          <input
            type="password"
            placeholder="Enter current password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            className="border p-2 rounded w-full focus:ring-2 focus:ring-green-600 outline-none bg-white dark:bg-gray-900 dark:border-gray-700"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">New Password</label>
          <input
            type="password"
            placeholder="Enter new password (min 6 characters)"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="border p-2 rounded w-full focus:ring-2 focus:ring-green-600 outline-none bg-white dark:bg-gray-900 dark:border-gray-700"
            minLength={6}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Confirm New Password</label>
          <input
            type="password"
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="border p-2 rounded w-full focus:ring-2 focus:ring-green-600 outline-none bg-white dark:bg-gray-900 dark:border-gray-700"
            minLength={6}
          />
        </div>

        <button
          disabled={loading}
          onClick={handleResetPassword}
          className={`w-full mt-2 px-6 py-2 rounded text-white font-semibold ${
            loading ? "bg-gray-400 cursor-not-allowed" : "bg-green-700 hover:bg-green-800"
          }`}
        >
          {loading ? "Resetting..." : "Reset Password"}
        </button>
      </div>
    </div>
  );
}
